// components/PublicLeadFormContent.tsx
import React, { useState } from 'react';
import { CheckCircle2, Loader2, Send, ArrowRight, ArrowLeft, Check } from 'lucide-react';
import toast from 'react-hot-toast';
import { PublicLeadFormData, LeadFormStep } from '../services/api';

export const DEFAULT_LEAD_FORM_STEPS: LeadFormStep[] = [
  {
    id: 'step_1',
    title: 'Informação Inicial',
    description: 'Identificação & Contacto Rápido',
    fields: [
      { id: 'f_name', label: 'O seu Nome', type: 'text', placeholder: 'Ex: Carlos Tembe', required: true, targetField: 'name' },
      { id: 'f_company', label: 'Nome da Empresa', type: 'text', placeholder: 'Ex: Comercial Maputo Lda', required: true, targetField: 'companyName' },
      { id: 'f_whatsapp', label: 'WhatsApp', type: 'phone', placeholder: '+258 84 000 0000', required: true, targetField: 'whatsapp' },
      { id: 'f_email', label: 'E-mail', type: 'email', placeholder: 'empresa@email.com', required: false, targetField: 'email' },
    ],
  },
  {
    id: 'step_2',
    title: 'O seu Negócio',
    description: 'Diagnóstico de Necessidades',
    fields: [
      {
        id: 'f_segment',
        label: 'Qual o segmento da sua empresa?',
        type: 'select',
        required: false,
        targetField: 'segment',
        options: [
          'Construção/Engenharia',
          'Imobiliária',
          'Consultoria',
          'Tecnologia/Agência',
          'Retalho/Loja',
          'Distribuição',
          'Restaurante/Hotelaria',
          'Serviços',
          'Educação/Formação',
          'Outro',
        ],
      },
      {
        id: 'f_challenges',
        label: 'Principais desafios',
        type: 'multiselect',
        required: false,
        targetField: 'challenges',
        options: [
          'Gestão de vendas',
          'POS/Caixa',
          'Stock',
          'Cotações e facturação',
          'Gestão de clientes/leads',
          'Comissões de vendedores',
          'Loja online',
          'Gestão comercial geral',
          'Outro',
        ],
      },
      {
        id: 'f_process',
        label: 'Como gere a operação atualmente?',
        type: 'radio',
        required: false,
        targetField: 'currentProcess',
        options: [
          'Excel/Word',
          'WhatsApp',
          'Sistema próprio',
          'Outro software',
          'Manualmente',
          'Não temos um sistema',
        ],
      },
    ],
  },
  {
    id: 'step_3',
    title: 'Detalhes Finais',
    description: 'Contexto Final & Submissão',
    fields: [
      { id: 'f_improvement', label: 'O que é prioritário melhorar hoje?', type: 'text', placeholder: 'Ex: Relatórios automáticos no final do dia', required: false, targetField: 'improvement' },
      { id: 'f_decisor', label: 'Nome do Responsável / Decisor', type: 'text', placeholder: 'Quem toma a decisão', required: false, targetField: 'decisionMaker' },
      {
        id: 'f_besttime',
        label: 'Melhor Horário para Contacto',
        type: 'select',
        required: false,
        targetField: 'bestTime',
        options: ['Manhã', 'Tarde', 'Qualquer horário'],
      },
      {
        id: 'f_channel',
        label: 'Como encontrou o MPV?',
        type: 'radio',
        required: false,
        targetField: 'channel',
        options: [
          'Instagram',
          'Facebook',
          'LinkedIn',
          'WhatsApp',
          'Indicação',
          'Pesquisa Google',
          'Contacto directo',
          'Outro',
        ],
      },
    ],
  },
];

const templateBg: Record<string, string> = {
  modern: 'bg-slate-950',
  classic: 'bg-slate-100',
  minimal: 'bg-slate-50',
  vibrant: 'bg-gradient-to-br from-indigo-900 via-purple-900 to-slate-900',
};

const cardStyle: Record<string, string> = {
  modern: 'bg-white shadow-2xl border border-slate-100',
  classic: 'bg-white shadow-xl',
  minimal: 'bg-white shadow-sm border border-slate-200',
  vibrant: 'bg-white/95 backdrop-blur-md',
};

interface Props {
  data: PublicLeadFormData;
  onSubmit: (payload: Record<string, any>) => Promise<void>;
  submitting: boolean;
  submitted: boolean;
  submittedMessage?: string;
}

export const PublicLeadFormContent: React.FC<Props> = ({
  data,
  onSubmit,
  submitting,
  submitted,
  submittedMessage,
}) => {
  const { form, company } = data;
  const design = form.design || {
    templateId: 'modern',
    primaryColor: '#4f46e5',
    accentColor: '#10b981',
    showLogo: true,
    radius: 'rounded-2xl',
    fieldStyle: 'outlined',
  };

  const steps = form.steps && form.steps.length > 0 ? form.steps : DEFAULT_LEAD_FORM_STEPS;
  const [currentStepIndex, setCurrentStepIndex] = useState<number>(0);
  const [answers, setAnswers] = useState<Record<string, any>>({});

  const currentStep = steps[currentStepIndex] || steps[0];

  const updateAnswer = (key: string, value: any) => {
    setAnswers((prev) => ({ ...prev, [key]: value }));
  };

  const toggleMultiselectAnswer = (key: string, option: string) => {
    setAnswers((prev) => {
      const existing: string[] = Array.isArray(prev[key]) ? prev[key] : [];
      const updated = existing.includes(option)
        ? existing.filter((item) => item !== option)
        : [...existing, option];
      return { ...prev, [key]: updated };
    });
  };

  const validateCurrentStep = (): boolean => {
    if (!currentStep) return true;
    for (const field of currentStep.fields) {
      if (field.required) {
        const val = answers[field.targetField || field.label];
        if (val === undefined || val === null || val === '' || (Array.isArray(val) && val.length === 0)) {
          toast.error(`O campo "${field.label}" é obrigatório`);
          return false;
        }
      }
    }
    return true;
  };

  const handleNext = (e: React.FormEvent) => {
    e.preventDefault();
    if (!validateCurrentStep()) return;
    if (currentStepIndex < steps.length - 1) {
      setCurrentStepIndex((prev) => prev + 1);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!validateCurrentStep()) return;

    // Extrair os campos chave para o payload
    const payload: Record<string, any> = { answers };

    // Tentar preencher name, whatsapp, email, companyName se mapeados
    steps.forEach((st) => {
      st.fields.forEach((f) => {
        const val = answers[f.targetField || f.label];
        if (f.targetField === 'name' || f.label.toLowerCase().includes('nome')) {
          if (!payload.name && typeof val === 'string') payload.name = val;
        }
        if (f.targetField === 'whatsapp' || f.targetField === 'phone' || f.label.toLowerCase().includes('whatsapp')) {
          if (!payload.whatsapp && typeof val === 'string') payload.whatsapp = val;
        }
        if (f.targetField === 'email' || f.label.toLowerCase().includes('email')) {
          if (!payload.email && typeof val === 'string') payload.email = val;
        }
        if (f.targetField === 'companyName' || f.label.toLowerCase().includes('empresa')) {
          if (!payload.companyName && typeof val === 'string') payload.companyName = val;
        }
      });
    });

    await onSubmit({ ...answers, ...payload });
  };

  if (submitted) {
    return (
      <div className={`min-h-screen ${templateBg[design.templateId] || 'bg-slate-900'} flex items-center justify-center p-4`}>
        <div className={`${cardStyle[design.templateId]} rounded-3xl p-8 sm:p-12 max-w-md w-full text-center animate-in fade-in zoom-in-95 duration-300`}>
          <div className="w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-5" style={{ backgroundColor: `${design.accentColor}20` }}>
            <CheckCircle2 size={40} style={{ color: design.accentColor }} />
          </div>
          <h2 className="text-2xl font-bold text-slate-900 mb-2">Pedido Recebido!</h2>
          <p className="text-slate-600 text-sm leading-relaxed mb-6">
            {submittedMessage || 'Obrigado pelo interesse. Um especialista da nossa equipa entrará em contacto muito em breve via WhatsApp.'}
          </p>
          <div className="p-3 bg-slate-50 rounded-xl border border-slate-100 text-xs text-slate-500">
            Tempo estimado de resposta: <strong>menos de 2 horas</strong>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className={`min-h-screen ${templateBg[design.templateId] || 'bg-slate-950'} py-8 px-4 flex flex-col justify-center items-center`}>
      <div className={`w-full max-w-xl ${cardStyle[design.templateId]} ${design.radius || 'rounded-2xl'} p-6 sm:p-10 transition-all duration-300 shadow-2xl`}>
        
        {/* Header */}
        <div className="text-center mb-6">
          {design.showLogo && (
            <div className="mb-4">
              {company.logo ? (
                <img src={company.logo} alt={company.name} className="h-12 w-auto mx-auto object-contain" />
              ) : (
                <div
                  className="h-12 w-12 rounded-xl mx-auto flex items-center justify-center text-white font-bold text-xl shadow-md"
                  style={{ backgroundColor: design.primaryColor }}
                >
                  {(company.name || 'M').charAt(0).toUpperCase()}
                </div>
              )}
            </div>
          )}
          
          <h1 className="text-xl sm:text-2xl font-bold text-slate-900 leading-snug">
            {form.title || 'Transforme a gestão da sua empresa com o MPV'}
          </h1>
          {form.subtitle && (
            <p className="text-xs sm:text-sm text-slate-500 mt-1">
              {form.subtitle}
            </p>
          )}
        </div>

        {/* Dynamic Progress Bar */}
        {steps.length > 1 && (
          <div className="mb-8">
            <div className="flex justify-between items-center text-xs font-semibold text-slate-400 mb-2 overflow-x-auto gap-2">
              {steps.map((st, idx) => (
                <span
                  key={st.id || idx}
                  className={`truncate max-w-[120px] ${idx <= currentStepIndex ? 'text-slate-800 font-bold' : ''}`}
                >
                  {idx + 1}. {st.title || `Passo ${idx + 1}`}
                </span>
              ))}
            </div>
            <div className="w-full bg-slate-100 h-2 rounded-full overflow-hidden">
              <div
                className="h-full transition-all duration-500 ease-out rounded-full"
                style={{
                  width: `${((currentStepIndex + 1) / steps.length) * 100}%`,
                  backgroundColor: design.primaryColor,
                }}
              />
            </div>
          </div>
        )}

        {/* Step Header */}
        {currentStep && (
          <div className="mb-4">
            <h3 className="font-semibold text-slate-800 text-base">{currentStep.title}</h3>
            {currentStep.description && (
              <p className="text-xs text-slate-500 mt-0.5">{currentStep.description}</p>
            )}
          </div>
        )}

        {/* Form Container */}
        <form onSubmit={currentStepIndex === steps.length - 1 ? handleSubmit : handleNext} className="space-y-5">
          <div className="space-y-4 animate-in fade-in slide-in-from-right-4 duration-300">
            {currentStep?.fields.map((field) => {
              const fieldKey = field.targetField || field.label;
              const val = answers[fieldKey] ?? '';

              return (
                <div key={field.id}>
                  <label className="block text-xs font-semibold text-slate-700 uppercase tracking-wider mb-1.5">
                    {field.label} {field.required && <span style={{ color: design.primaryColor }}>*</span>}
                  </label>

                  {/* Input Types */}
                  {field.type === 'text' && (
                    <input
                      type="text"
                      required={field.required}
                      placeholder={field.placeholder || ''}
                      value={val}
                      onChange={(e) => updateAnswer(fieldKey, e.target.value)}
                      className="w-full rounded-xl border border-slate-200 bg-slate-50/50 px-4 py-3 text-sm text-slate-900 placeholder:text-slate-400 focus:bg-white focus:outline-none focus:ring-2 focus:border-transparent transition"
                      style={{ '--tw-ring-color': design.primaryColor } as React.CSSProperties}
                    />
                  )}

                  {field.type === 'textarea' && (
                    <textarea
                      required={field.required}
                      rows={3}
                      placeholder={field.placeholder || ''}
                      value={val}
                      onChange={(e) => updateAnswer(fieldKey, e.target.value)}
                      className="w-full rounded-xl border border-slate-200 bg-slate-50/50 px-4 py-3 text-sm text-slate-900 placeholder:text-slate-400 focus:bg-white focus:outline-none focus:ring-2 focus:border-transparent transition"
                      style={{ '--tw-ring-color': design.primaryColor } as React.CSSProperties}
                    />
                  )}

                  {field.type === 'number' && (
                    <input
                      type="number"
                      required={field.required}
                      placeholder={field.placeholder || ''}
                      value={val}
                      onChange={(e) => updateAnswer(fieldKey, e.target.value)}
                      className="w-full rounded-xl border border-slate-200 bg-slate-50/50 px-4 py-3 text-sm text-slate-900 placeholder:text-slate-400 focus:bg-white focus:outline-none focus:ring-2 focus:border-transparent transition"
                      style={{ '--tw-ring-color': design.primaryColor } as React.CSSProperties}
                    />
                  )}

                  {field.type === 'phone' && (
                    <input
                      type="tel"
                      required={field.required}
                      placeholder={field.placeholder || '+258 84 000 0000'}
                      value={val}
                      onChange={(e) => updateAnswer(fieldKey, e.target.value)}
                      className="w-full rounded-xl border border-slate-200 bg-slate-50/50 px-4 py-3 text-sm text-slate-900 placeholder:text-slate-400 focus:bg-white focus:outline-none focus:ring-2 focus:border-transparent transition"
                      style={{ '--tw-ring-color': design.primaryColor } as React.CSSProperties}
                    />
                  )}

                  {field.type === 'email' && (
                    <input
                      type="email"
                      required={field.required}
                      placeholder={field.placeholder || 'exemplo@email.com'}
                      value={val}
                      onChange={(e) => updateAnswer(fieldKey, e.target.value)}
                      className="w-full rounded-xl border border-slate-200 bg-slate-50/50 px-4 py-3 text-sm text-slate-900 placeholder:text-slate-400 focus:bg-white focus:outline-none focus:ring-2 focus:border-transparent transition"
                      style={{ '--tw-ring-color': design.primaryColor } as React.CSSProperties}
                    />
                  )}

                  {field.type === 'date' && (
                    <input
                      type="date"
                      required={field.required}
                      value={val}
                      onChange={(e) => updateAnswer(fieldKey, e.target.value)}
                      className="w-full rounded-xl border border-slate-200 bg-slate-50/50 px-4 py-3 text-sm text-slate-900 focus:bg-white focus:outline-none focus:ring-2 focus:border-transparent transition"
                      style={{ '--tw-ring-color': design.primaryColor } as React.CSSProperties}
                    />
                  )}

                  {field.type === 'select' && (
                    <select
                      required={field.required}
                      value={val}
                      onChange={(e) => updateAnswer(fieldKey, e.target.value)}
                      className="w-full rounded-xl border border-slate-200 bg-slate-50/50 px-4 py-3 text-sm text-slate-900 focus:bg-white focus:outline-none focus:ring-2 focus:border-transparent transition"
                    >
                      <option value="">Selecione uma opção...</option>
                      {(field.options || []).map((opt) => (
                        <option key={opt} value={opt}>{opt}</option>
                      ))}
                    </select>
                  )}

                  {field.type === 'radio' && (
                    <div className="flex flex-wrap gap-2 pt-1">
                      {(field.options || []).map((opt) => {
                        const selected = val === opt;
                        return (
                          <button
                            type="button"
                            key={opt}
                            onClick={() => updateAnswer(fieldKey, opt)}
                            className={`px-3.5 py-2 rounded-full border text-xs font-medium transition ${
                              selected
                                ? 'border-transparent text-white shadow-sm'
                                : 'border-slate-200 bg-slate-50 text-slate-600 hover:bg-slate-100'
                            }`}
                            style={selected ? { backgroundColor: design.primaryColor } : undefined}
                          >
                            {opt}
                          </button>
                        );
                      })}
                    </div>
                  )}

                  {field.type === 'multiselect' && (
                    <div className="grid grid-cols-2 gap-2 pt-1">
                      {(field.options || []).map((opt) => {
                        const selected = Array.isArray(val) && val.includes(opt);
                        return (
                          <button
                            type="button"
                            key={opt}
                            onClick={() => toggleMultiselectAnswer(fieldKey, opt)}
                            className={`text-left px-3 py-2.5 rounded-xl border text-xs font-medium flex items-center justify-between transition ${
                              selected
                                ? 'border-transparent text-white shadow-sm'
                                : 'border-slate-200 bg-slate-50/50 text-slate-700 hover:bg-slate-100'
                            }`}
                            style={selected ? { backgroundColor: design.primaryColor } : undefined}
                          >
                            <span>{opt}</span>
                            {selected && <Check size={14} className="shrink-0 ml-1" />}
                          </button>
                        );
                      })}
                    </div>
                  )}
                </div>
              );
            })}
          </div>

          {/* Navigation Controls */}
          <div className="flex gap-3 pt-3">
            {currentStepIndex > 0 && (
              <button
                type="button"
                onClick={() => setCurrentStepIndex((prev) => Math.max(prev - 1, 0))}
                className="py-3.5 px-4 rounded-xl border border-slate-200 text-slate-600 text-sm font-medium hover:bg-slate-50 transition flex items-center justify-center"
              >
                <ArrowLeft size={18} />
              </button>
            )}

            {currentStepIndex < steps.length - 1 ? (
              <button
                type="submit"
                className="flex-1 py-3.5 px-6 rounded-xl text-white font-medium text-sm flex items-center justify-center gap-2 shadow-lg hover:brightness-110 active:scale-[0.99] transition"
                style={{ backgroundColor: design.primaryColor }}
              >
                Continuar
                <ArrowRight size={18} />
              </button>
            ) : (
              <button
                type="submit"
                disabled={submitting}
                className="flex-1 py-3.5 px-6 rounded-xl text-white font-semibold text-sm flex items-center justify-center gap-2 shadow-xl hover:brightness-110 active:scale-[0.99] transition disabled:opacity-60"
                style={{ backgroundColor: design.accentColor }}
              >
                {submitting ? (
                  <Loader2 size={18} className="animate-spin" />
                ) : (
                  <>
                    <Send size={18} />
                    {form.ctaText || 'Solicitar Demonstração Gratuita'}
                  </>
                )}
              </button>
            )}
          </div>
        </form>

        {/* Footer info */}
        <div className="mt-8 pt-4 border-t border-slate-100 flex items-center justify-between text-xs text-slate-400">
          <span>🔒 Dados 100% Protegidos</span>
          <span>{company.name}</span>
        </div>
      </div>
    </div>
  );
};